(() => {
  if (customElements.get('cc-newsletter-popup')) return;
  const storageKey = 'wm_newsletter_popup_client_v2';
  const pendingKey = 'wm_newsletter_pending_v1';
  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.backdrop = this.querySelector('[data-cc-newsletter-backdrop]');
      this.form = this.querySelector('form');
      this.designMode = this.dataset.designMode === 'true';
      this.success = !!this.querySelector('[data-cc-newsletter-success]');
      this.error = !!this.querySelector('[data-cc-newsletter-error]');
      this.attempts = 0;
      this.querySelectorAll('[data-cc-newsletter-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), { signal }));
      this.backdrop.addEventListener('click', () => { if (!this.captchaVisible()) this.dialog.close(); }, { signal });
      document.addEventListener('keydown', event => {
        if (!this.dialog.open || this.captchaVisible()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) return;
        if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cc-newsletter-open');
        this.backdrop.hidden = true;
        this.dialog.setAttribute('aria-modal', 'false');
        if (this.captchaObserver) this.captchaObserver.disconnect();
        if (!this.success && !this.designMode) this.remember('dismissed', Number(this.dataset.dismissDays) || 7);
        if (this.opener && this.opener !== document.body && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      }, { signal });
      this.form.addEventListener('submit', () => {
        try { sessionStorage.setItem(pendingKey, String(Date.now())); } catch (_) {}
      }, { signal });
      document.addEventListener('cc:newsletter:open', () => this.open(true), { signal });
      document.addEventListener('shopify:section:select', event => {
        if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.open(true);
      }, { signal });
      let pending = 0;
      try { pending = Number(sessionStorage.getItem(pendingKey)) || 0; } catch (_) {}
      const returnedToPopup = location.hash === '#' + this.dataset.formId || (pending > 0 && Date.now() - pending < 600000);
      if (this.success) {
        this.remember('subscribed', 365);
        try { sessionStorage.removeItem(pendingKey); } catch (_) {}
        if (returnedToPopup) this.open(true);
        return;
      }
      if (this.error && returnedToPopup) {
        try { sessionStorage.removeItem(pendingKey); } catch (_) {}
        this.open(true);
        this.querySelector('[data-cc-newsletter-error]').focus();
        return;
      }
      if (location.hash === '#newsletter-signup') {
        this.open(true);
        return;
      }
      if (this.designMode || this.error || this.dataset.autoShow !== 'true' || this.suppressed()) return;
      this.timer = setTimeout(() => this.autoOpen(), Math.max(5, Number(this.dataset.delay) || 30) * 1000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      if (this.captchaObserver) this.captchaObserver.disconnect();
      if (this.backdrop) this.backdrop.hidden = true;
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        document.documentElement.classList.remove('cc-newsletter-open');
      }
      this.initialized = false;
    }
    suppressed() {
      try {
        const state = JSON.parse(localStorage.getItem(storageKey) || 'null');
        const previous = localStorage.getItem('wm_newsletter_popup_v1');
        return !!(state || previous);
      } catch (_) {
        try { return !!sessionStorage.getItem(storageKey); } catch (_) { return false; }
      }
    }
    remember(status, days) {
      const until = Date.now() + days * 86400000;
      try { localStorage.setItem(storageKey, JSON.stringify({ status, until })); }
      catch (_) { try { sessionStorage.setItem(storageKey, String(until)); } catch (_) {} }
    }
    captchaVisible() {
      return Array.from(document.querySelectorAll('iframe')).some(frame => {
        if (!/captcha challenge/i.test(frame.title)) return false;
        const box = frame.getBoundingClientRect();
        const style = getComputedStyle(frame);
        return box.width > 0 && box.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
      });
    }
    autoOpen() {
      if (this.suppressed() || !this.isConnected) return;
      const active = document.activeElement;
      const occupied = this.captchaVisible() || document.querySelector('dialog[open]') || document.hidden || (active && (active.matches('input, textarea, select') || active.isContentEditable));
      if (occupied) {
        this.attempts += 1;
        if (this.attempts < 7) this.timer = setTimeout(() => this.autoOpen(), 5000);
        return;
      }
      this.open(false);
    }
    open(force) {
      if (!this.dialog || this.dialog.open || (!force && this.suppressed())) return;
      const otherDialog = document.querySelector('dialog[open]');
      if (otherDialog && otherDialog !== this.dialog) return;
      clearTimeout(this.timer);
      this.opener = document.activeElement;
      this.backdrop.hidden = false;
      this.dialog.setAttribute('aria-modal', 'true');
      this.dialog.show();
      if (!this.designMode) this.remember('seen', 36500);
      this.captchaObserver = new MutationObserver(() => {
        this.dialog.setAttribute('aria-modal', this.captchaVisible() ? 'false' : 'true');
      });
      this.captchaObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style','class','hidden'] });
      this.querySelector('[data-cc-newsletter-close]').focus();
      document.documentElement.classList.add('cc-newsletter-open');
    }
  }
  customElements.define('cc-newsletter-popup', NewsletterPopup);
})();
